import logging from '@sstdev/lib_logging';
import { errors as _errors } from 'lib_ui-primitives';
import basicValidation from '../../namespace/relation/doingValidate_namespace_relation';

export default {
    verb: 'doingValidate',
    namespace: 'rulesEngine',
    relation: 'customerWorkflow',
    priority: (basicValidation.priority || 0) + 5, // we want this to run BEFORE basic validation
    description: 'validate new workflow creation',
    // this is the actual logic:
    logic
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @template {{ newRecord: { [key: string]: any } }} T
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {Promise<T>}
 */
async function logic({ data, context }) {
    const actions = data.newRecord.actions || [];

    // Separate validation rules per action type
    // 1. forward_item_item => requires a password
    validateForwardOverMqtt(actions, context);

    // 2. send_notification_http => requires a body and url
    validateSendNotificationHttp(actions, context);

    // 3. send_notification_email => requires a subject, recipients, and message
    validateSendEmail(actions, context);

    return data;
}

function validateForwardOverMqtt(actions, context) {
    const missingPassword = actions.filter(a => a.type === 'forward_item_item' && !a.password);
    if (missingPassword.length > 0) {
        const errorMessage = 'Forward Over MQTT actions must have a password set.';
        logging.error(errorMessage, { actions: missingPassword }, context);
        throw new _errors.ValidationError(errorMessage, {});
    }
}

/**
 * Validate that every action of type `send_notification_http` includes both a `url` and a `body` property.
 * Throws a ValidationError if invalid actions are found.
 *
 * @param {Array<{type?:string|undefined, workflowAction?:string|undefined, url?:string, body?:any}>} actions
 * @param {import("rulesengine.io").Context} context
 */
export function validateSendNotificationHttp(actions, context) {
    if (!Array.isArray(actions) || actions.length === 0) return;

    // The action discriminator for customerWorkflow is `type`, and for notificationRule (legacy) it is `workflowAction`
    const invalidActions = actions.filter(
        a =>
            (a.type === 'send_notification_http' || a.workflowAction === 'send_notification_http') &&
            (!a.url || !a.body)
    );

    if (invalidActions.length > 0) {
        const errorMessage = 'Send Notification HTTP actions must have a url and body set.';
        logging.error(errorMessage, { actions: invalidActions }, context);
        throw new _errors.ValidationError(errorMessage, {});
    }
}

export function validateSendEmail(actions, context) {
    if (!Array.isArray(actions) || actions.length === 0) return;

    const invalidActions = actions.filter(
        a =>
            (a.type === 'send_notification_email' || a.workflowAction === 'send_notification_email') &&
            (!a.recipients || !a.subject || !a.message)
    );

    if (invalidActions.length > 0) {
        const errorMessage = 'Send Email actions must have a subject, recipients, and email message set.';
        logging.error(errorMessage, { actions: invalidActions }, context);
        throw new _errors.ValidationError(errorMessage, {});
    }
}
